import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Ship } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Ship className="w-6 h-6" />
            <h1 className="text-xl font-bold">Terms of Service</h1>
          </div>
        </div>
      </div>

      <ScrollArea className="h-[calc(100vh-64px)]">
        <div className="max-w-3xl mx-auto p-6 space-y-6">
          <p className="text-sm text-gray-500">Last Updated: February 4, 2026</p>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">1. Acceptance of Terms</h2>
            <p className="text-gray-600">
              By downloading, installing, or using the Boat 360 mobile application ("App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">2. Description of Service</h2>
            <p className="text-gray-600">
              Boat 360 is a marine safety and social application that provides:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li>Real-time location sharing with friends and family</li>
              <li>Crowd-sourced hazard reporting</li>
              <li>Emergency SOS alerts</li>
              <li>Trip tracking and float plans</li>
              <li>Weather information</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">3. Important Safety Disclaimer</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-semibold">
                BOAT 360 IS NOT A REPLACEMENT FOR OFFICIAL EMERGENCY SERVICES.
              </p>
              <p className="text-red-700 mt-2">
                The SOS feature in this App does NOT contact the Coast Guard, 911, or any official emergency services. It only notifies other Boat 360 users in your area or your designated contacts. In a real emergency, always contact official emergency services directly.
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">4. User Accounts</h2>
            <p className="text-gray-600">
              You must create an account to use certain features of the App. You are responsible for:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Providing accurate and current information</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">5. Subscription Services (Boat 360 Plus)</h2>
            <p className="text-gray-600">
              Boat 360 Plus is an auto-renewable subscription that provides premium features including friend tracking, background location updates, and private SOS alerts.
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li><strong>Price:</strong> $19.99 USD per year</li>
              <li><strong>Billing:</strong> Payment will be charged to your Apple ID or Google Play account at confirmation of purchase</li>
              <li><strong>Renewal:</strong> Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current period</li>
              <li><strong>Management:</strong> You can manage or cancel your subscription in your device's account settings</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">6. Acceptable Use</h2>
            <p className="text-gray-600">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li>Use the App for any unlawful purpose</li>
              <li>Submit false hazard reports or fake SOS alerts</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Attempt to gain unauthorized access to any part of the App</li>
              <li>Use the App while operating a vessel in an unsafe manner</li>
              <li>Share your location with malicious intent</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">7. Location Data</h2>
            <p className="text-gray-600">
              The App collects and uses your location data to provide its core services. By using the App, you consent to the collection and use of your location data as described in our Privacy Policy. You can control location sharing through the App's settings and your device's permissions.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">8. User-Generated Content</h2>
            <p className="text-gray-600">
              You retain ownership of content you submit (hazard reports, photos, etc.), but grant us a worldwide, non-exclusive license to use, display, and distribute this content in connection with the App's services. You are solely responsible for the accuracy of content you submit.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">9. Disclaimer of Warranties</h2>
            <p className="text-gray-600">
              THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. LOCATION DATA, WEATHER INFORMATION, AND HAZARD REPORTS MAY NOT BE ACCURATE AND SHOULD NOT BE RELIED UPON AS THE SOLE SOURCE OF NAVIGATION INFORMATION.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">10. Limitation of Liability</h2>
            <p className="text-gray-600">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO PERSONAL INJURY, PROPERTY DAMAGE, OR LOSS OF DATA, ARISING FROM YOUR USE OF THE APP.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">11. Indemnification</h2>
            <p className="text-gray-600">
              You agree to indemnify and hold harmless Boat 360, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the App or violation of these Terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">12. Changes to Terms</h2>
            <p className="text-gray-600">
              We may modify these Terms at any time. Continued use of the App after changes constitutes acceptance of the modified Terms. We will notify users of significant changes through the App or via email.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">13. Termination</h2>
            <p className="text-gray-600">
              We may suspend or terminate your access to the App at any time for violation of these Terms or for any other reason. Upon termination, your right to use the App ceases immediately.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">14. Governing Law</h2>
            <p className="text-gray-600">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">15. Contact Information</h2>
            <p className="text-gray-600">
              For questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-600">
              Email: support@boat360.app<br />
              Website: https://boat360.app
            </p>
          </section>

          <div className="pt-6 border-t">
            <p className="text-sm text-gray-500 text-center">
              © 2026 Boat 360. All rights reserved.
            </p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default TermsOfService;
